'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { TelaDeRedeBloqueada } from '@/components/RedeBloqueada/TelaDeRedeBloqueada'
import { esperaDaTentativaMs, iniciarAgendador } from '@/lib/auth/agendador'
import { bloquear, estaBloqueada, ouvirRedeBloqueada } from '@/lib/auth/redeBloqueada'
import { ligarAcessoDasOutrasAbas, renovar } from '@/lib/auth/renovacao'
import { encerrarSessaoLocal, sairPorSessaoMorta } from '@/lib/auth/saida'
import { sairNoServidor } from '@/lib/auth/saidaNoServidor'
import { anunciarOperacao } from '@/lib/auth/sessaoEntreAbas'
import { clearAuthStorage, limparLegado } from '@/lib/auth/storage'
import { loginComVolta } from '@/lib/auth/voltaAposLogin'

type EstadoDoPortao = 'carregando' | 'sem-conexao' | 'rede-bloqueada' | 'pronta'

export const TEXTO_CARREGANDO = 'Carregando…'
export const TEXTO_SEM_CONEXAO = 'Não foi possível conectar. Tentando de novo…'

/** Sem sessão na abertura: /login SEM aviso — não havia sessão nesta aba (§4, I8). Leva a tela pedida no
 *  `?voltar` pra o login devolver depois de entrar (link aberto sem sessão). */
function irAoLoginSemAviso(): void {
  clearAuthStorage()
  window.location.assign(loginComVolta(window.location))
}

/**
 * "Sair" na tela de bloqueio da ABERTURA: o `AuthProvider` (dono do logout) ainda não montou. A mesma sequência do
 * `AuthContext.logout`, sem o socket (que também não existe): anuncia → avisa o servidor → saída local. Fora da rede e
 * sem access na memória o aviso não confirma (o refresh é recusado) e fica a saída pendente.
 */
async function sairNaAbertura(): Promise<void> {
  anunciarOperacao('sair')
  const confirmacao = await sairNoServidor('logout')
  encerrarSessaoLocal({ motivo: 'saiu', avisarNestaAba: false, saidaPendente: confirmacao === 'nao-confirmada' })
}

/**
 * A área privada só monta depois da renovação inicial (faixa 25 §8). Ver `PortaoDeSessao.md`.
 *
 * O access vive só em memória: todo carregamento (F5, aba nova, navegação completa pós-login) começa sem ele e
 * renova pelo cookie HttpOnly da API. Liga a ponte entre abas e o agendador ANTES de renovar — o access inicial
 * (desta renovação ou entregue por outra aba que renovou primeiro) já agenda a próxima.
 */
export function PortaoDeSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoDoPortao>('carregando')

  useEffect(() => {
    limparLegado()
    const desligarPonte = ligarAcessoDasOutrasAbas()
    const pararAgendador = iniciarAgendador({ aoPerderSessao: sairPorSessaoMorta })
    // Faixa 28 (D4): QUALQUER `liberar()` — o "Tentar de novo", outra aba que renovou (a ponte), uma renovação — tira
    // a abertura do bloqueio. Com o app já montado o portão não faz nada: quem cobre é o `BloqueioDeRede`.
    const desligarRede = ouvirRedeBloqueada(() => {
      if (!estaBloqueada()) setEstado((atual) => (atual === 'rede-bloqueada' ? 'pronta' : atual))
    })
    let montado = true
    let novaTentativa: ReturnType<typeof setTimeout> | null = null
    let tentativa = 0

    const tentar = async (): Promise<void> => {
      const resultado = await renovar()
      if (!montado) return
      if (resultado.tipo === 'renovada') {
        setEstado('pronta')
        return
      }
      if (resultado.tipo === 'rede-bloqueada') {
        // O cookie vale, a rede não. A tela SEM os filhos: na abertura não há o que preservar, e filhos montados
        // disparariam 401 + renovação à toa. `bloquear()` aqui também: quem sai é o store.
        bloquear()
        setEstado('rede-bloqueada')
        return
      }
      if (resultado.tipo === 'indisponivel') {
        // §4: carga inicial não tem tela a manter — fica no carregamento e tenta de novo, nunca vai ao /login.
        setEstado('sem-conexao')
        novaTentativa = setTimeout(() => void tentar(), esperaDaTentativaMs(tentativa))
        tentativa += 1
        return
      }
      // Sem sessão (ou com saída pendente que não concluiu): /login SEM aviso — não havia sessão nesta aba (§4, I8).
      irAoLoginSemAviso()
    }
    void tentar()

    return () => {
      montado = false
      if (novaTentativa !== null) clearTimeout(novaTentativa)
      desligarPonte()
      desligarRede()
      pararAgendador()
    }
  }, [])

  if (estado === 'pronta') return <>{children}</>
  if (estado === 'rede-bloqueada') return <TelaDeRedeBloqueada aoPerderSessao={irAoLoginSemAviso} aoSair={sairNaAbertura} />
  return (
    <div role="status" className="flex h-dvh w-full items-center justify-center text-sm text-muted-foreground">
      {estado === 'sem-conexao' ? TEXTO_SEM_CONEXAO : TEXTO_CARREGANDO}
    </div>
  )
}
